/**
 * lib/bursForms.ts
 * ------------------------------------------------------------------
 * BURS STATUTORY FORMS — pure aggregation engine.
 *
 * Botswana's tax year runs 1 July → 30 June. BURS PAYE reporting is:
 *   - ITW-7  monthly PAYE return        (one month of payslips)
 *   - ITW-8  employee annual certificate (one tax year, per employee)
 *   - ITW-10 annual PAYE reconciliation  (one tax year, all employees)
 *
 * Every figure here is DERIVED from the immutable generated payslips
 * (the system of record — what was actually withheld and remitted),
 * never recomputed from salary. That keeps the forms consistent with
 * what staff were paid and what DeductionReports already shows. The
 * math lives in pure functions so the screens, the CSVs and the PDFs
 * all agree to the thebe.
 *
 * Tax-year identity convention: a tax year is named by the YEAR IT
 * ENDS. endYear 2026 == "1 July 2025 – 30 June 2026".
 *
 * Removable with the rest of the HR/payroll domain.
 * ------------------------------------------------------------------ */
import { SCHOOL } from '@/lib/school.config';
import { round2, MONTH_LABELS, type HrPayslip, type HrPayrollSettings } from '@/lib/hrPayroll';

/* ==================================================================
   TAX-YEAR ARITHMETIC (1 July – 30 June)
   ================================================================== */

export interface TaxYear {
  /** Year the tax year ENDS, e.g. 2026 for 1 Jul 2025 – 30 Jun 2026. */
  endYear: number;
  startISO: string; // YYYY-07-01
  endISO: string; // YYYY-06-30
  /** Full label, e.g. "1 July 2025 – 30 June 2026". */
  label: string;
  /** Short label, e.g. "FY ending 30 June 2026". */
  shortLabel: string;
}

/** Which tax-year-end a calendar period falls in. Jul–Dec → next year. */
export function taxYearEndYearOf(period: { year: number; month: number }): number {
  return period.month >= 7 ? period.year + 1 : period.year;
}

/** Build the descriptor for a tax year identified by its end year. */
export function taxYear(endYear: number): TaxYear {
  const startYear = endYear - 1;
  return {
    endYear,
    startISO: `${startYear}-07-01`,
    endISO: `${endYear}-06-30`,
    label: `1 July ${startYear} – 30 June ${endYear}`,
    shortLabel: `FY ending 30 June ${endYear}`,
  };
}

/** The 12 calendar periods of a tax year, in order Jul → Jun. */
export function taxYearMonths(endYear: number): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  for (let m = 7; m <= 12; m++) out.push({ year: endYear - 1, month: m });
  for (let m = 1; m <= 6; m++) out.push({ year: endYear, month: m });
  return out;
}

/** True if a calendar period belongs to the given tax year. */
export function periodInTaxYear(
  period: { year: number; month: number },
  endYear: number,
): boolean {
  return taxYearEndYearOf(period) === endYear;
}

/** Human month label, e.g. {2025,7} → "July 2025". */
export function monthYearLabel(year: number, month: number): string {
  return `${MONTH_LABELS[month - 1] ?? month} ${year}`;
}

/* ==================================================================
   PER-PAYSLIP TAX FIGURES
   ------------------------------------------------------------------
   Gross emoluments come from earningsTotal. Withheld tax = the PAYE
   line + any lump-sum (gratuity) tax on that slip; both are income
   tax remitted to BURS, surfaced separately for transparency.
   In Phase-1 scope gross == taxable (no pre-tax deductions), so the
   forms label the figure "Gross emoluments" and treat it as taxable.
   ================================================================== */

export interface SlipTaxFigures {
  gross: number;
  paye: number;
  gratuityTax: number;
  totalTax: number;
}

export function slipTaxFigures(p: HrPayslip): SlipTaxFigures {
  const gross = round2(Number(p.earningsTotal) || 0);
  const paye = round2(Number(p.paye) || 0);
  const gratuityTax = round2(Number(p.gratuity?.tax) || 0);
  return { gross, paye, gratuityTax, totalTax: round2(paye + gratuityTax) };
}

/* ==================================================================
   ITW-7 — MONTHLY PAYE RETURN
   ================================================================== */

export interface EmployeeMonthRow {
  staffId: string;
  employeeNo: string;
  name: string;
  jobTitle?: string;
  gross: number;
  paye: number;
  gratuityTax: number;
  totalTax: number;
}

export interface MonthlyReturn {
  year: number;
  month: number;
  monthLabel: string;
  rows: EmployeeMonthRow[];
  totals: { employees: number; gross: number; paye: number; gratuityTax: number; totalTax: number };
  /** True if any slip in the month carried lump-sum (gratuity) tax. */
  hasGratuityTax: boolean;
}

/**
 * Aggregate one month of payslips into a PAYE return. One row per staff
 * member (duplicate slips for the same staff in a month are summed, which
 * should not happen but is handled defensively).
 */
export function buildMonthlyReturn(
  slips: HrPayslip[],
  year: number,
  month: number,
): MonthlyReturn {
  const inMonth = slips.filter((p) => p.period?.year === year && p.period?.month === month);
  const byStaff = new Map<string, EmployeeMonthRow>();
  for (const p of inMonth) {
    const f = slipTaxFigures(p);
    const existing = byStaff.get(p.staffId);
    if (existing) {
      existing.gross = round2(existing.gross + f.gross);
      existing.paye = round2(existing.paye + f.paye);
      existing.gratuityTax = round2(existing.gratuityTax + f.gratuityTax);
      existing.totalTax = round2(existing.totalTax + f.totalTax);
    } else {
      byStaff.set(p.staffId, {
        staffId: p.staffId,
        employeeNo: p.employeeNo,
        name: p.staffName,
        jobTitle: p.jobTitle,
        ...f,
      });
    }
  }
  const rows = Array.from(byStaff.values()).sort((a, b) => a.name.localeCompare(b.name));
  const totals = rows.reduce(
    (acc, r) => ({
      employees: acc.employees + 1,
      gross: round2(acc.gross + r.gross),
      paye: round2(acc.paye + r.paye),
      gratuityTax: round2(acc.gratuityTax + r.gratuityTax),
      totalTax: round2(acc.totalTax + r.totalTax),
    }),
    { employees: 0, gross: 0, paye: 0, gratuityTax: 0, totalTax: 0 },
  );
  return {
    year,
    month,
    monthLabel: monthYearLabel(year, month),
    rows,
    totals,
    hasGratuityTax: totals.gratuityTax > 0,
  };
}

/* ==================================================================
   ITW-8 / ITW-10 — ANNUAL PER-EMPLOYEE AGGREGATION
   ================================================================== */

export interface EmployeeAnnualRow {
  staffId: string;
  employeeNo: string;
  name: string;
  jobTitle?: string;
  department?: string;
  /** Number of months in the tax year this employee was paid. */
  monthsPaid: number;
  gross: number;
  paye: number;
  gratuityTax: number;
  totalTax: number;
}

/**
 * Per-employee annual totals across a tax year. Identity (name, job,
 * employee no) is taken from the latest slip in the year so a mid-year
 * change of title still reads correctly.
 */
export function buildAnnualByEmployee(
  slips: HrPayslip[],
  endYear: number,
): EmployeeAnnualRow[] {
  const inYear = slips.filter((p) => p.period && periodInTaxYear(p.period, endYear));
  const byStaff = new Map<string, EmployeeAnnualRow & { _latestIdx: number }>();
  for (const p of inYear) {
    const f = slipTaxFigures(p);
    const idx = (p.period.year * 12) + p.period.month;
    const existing = byStaff.get(p.staffId);
    if (existing) {
      existing.monthsPaid += 1;
      existing.gross = round2(existing.gross + f.gross);
      existing.paye = round2(existing.paye + f.paye);
      existing.gratuityTax = round2(existing.gratuityTax + f.gratuityTax);
      existing.totalTax = round2(existing.totalTax + f.totalTax);
      if (idx >= existing._latestIdx) {
        existing._latestIdx = idx;
        existing.name = p.staffName;
        existing.employeeNo = p.employeeNo;
        existing.jobTitle = p.jobTitle;
        existing.department = p.department;
      }
    } else {
      byStaff.set(p.staffId, {
        staffId: p.staffId,
        employeeNo: p.employeeNo,
        name: p.staffName,
        jobTitle: p.jobTitle,
        department: p.department,
        monthsPaid: 1,
        ...f,
        _latestIdx: idx,
      });
    }
  }
  return Array.from(byStaff.values())
    .map(({ _latestIdx, ...row }) => { void _latestIdx; return row; })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface MonthTotals {
  year: number;
  month: number;
  monthLabel: string;
  employees: number;
  gross: number;
  paye: number;
  gratuityTax: number;
  totalTax: number;
}

export interface AnnualReconciliation {
  taxYear: TaxYear;
  /** 12 rows, Jul → Jun (zeroed for months with no pay run yet). */
  months: MonthTotals[];
  byEmployee: EmployeeAnnualRow[];
  totals: { employees: number; gross: number; paye: number; gratuityTax: number; totalTax: number };
}

/** ITW-10 reconciliation: month-by-month totals + per-employee breakdown. */
export function buildAnnualReconciliation(
  slips: HrPayslip[],
  endYear: number,
): AnnualReconciliation {
  const months: MonthTotals[] = taxYearMonths(endYear).map(({ year, month }) => {
    const ret = buildMonthlyReturn(slips, year, month);
    return {
      year,
      month,
      monthLabel: monthYearLabel(year, month),
      employees: ret.totals.employees,
      gross: ret.totals.gross,
      paye: ret.totals.paye,
      gratuityTax: ret.totals.gratuityTax,
      totalTax: ret.totals.totalTax,
    };
  });
  const byEmployee = buildAnnualByEmployee(slips, endYear);
  const totals = {
    // Distinct employees across the year (not the sum of monthly counts).
    employees: byEmployee.length,
    gross: round2(months.reduce((s, m) => s + m.gross, 0)),
    paye: round2(months.reduce((s, m) => s + m.paye, 0)),
    gratuityTax: round2(months.reduce((s, m) => s + m.gratuityTax, 0)),
    totalTax: round2(months.reduce((s, m) => s + m.totalTax, 0)),
  };
  return { taxYear: taxYear(endYear), months, byEmployee, totals };
}

/* ==================================================================
   EMPLOYER DETAILS (read from settings, fall back to SCHOOL config)
   ------------------------------------------------------------------
   Persisted on the existing hrPayrollSettings singleton via the
   optional burs* fields. TIN has no school-config default — the
   bursar enters it once on the Employer Details tab.
   ================================================================== */

export interface BursEmployerDetails {
  name: string;
  tin: string;
  addressLines: string[];
}

export function resolveEmployerDetails(settings?: HrPayrollSettings): BursEmployerDetails {
  return {
    name: settings?.bursEmployerName?.trim() || SCHOOL.name,
    tin: settings?.bursEmployerTin?.trim() || '',
    addressLines:
      settings?.bursEmployerAddress && settings.bursEmployerAddress.length > 0
        ? settings.bursEmployerAddress
        : [...SCHOOL.contact.addressLines],
  };
}

/* ==================================================================
   CSV HELPERS
   ================================================================== */

export function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: (string | number)[][]): string {
  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
}

/** Trigger a browser download of CSV text. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
